"use client";

import { useEffect, useRef, useState } from "react";
import { usePathname } from "next/navigation";
import { LinkWithChannel } from "@/ui/atoms/link-with-channel";
import { cn } from "@/lib/utils";

/** Derive-state-during-render pattern: closes the panel when route changes,
 *  without setState in effect (React 18+ recommended). */
function useCloseOnPathnameChange(setOpen: (v: boolean) => void, clear: () => void) {
	const pathname = usePathname();
	const [prevPath, setPrevPath] = useState(pathname);
	if (prevPath !== pathname) {
		setPrevPath(pathname);
		setOpen(false);
		clear();
	}
}

export interface NavDropdownItem {
	href: string;
	label: string;
	sub?: string;
}

interface NavHoverDropdownProps {
	href: string;
	label: string;
	sectionLabel: string;
	items?: NavDropdownItem[];
	footer?: { href: string; label: string };
}

const OPEN_DELAY = 80;
const CLOSE_DELAY = 140;

/**
 * Clean hover-dropdown for the top nav. Opens on mouseenter (debounced), closes
 * on mouseleave with a small grace window so the cursor can travel to the panel.
 * Keyboard: focusable trigger opens panel via focus + closes via Escape.
 */
export function NavHoverDropdown({ href, label, sectionLabel, items, footer }: NavHoverDropdownProps) {
	const [open, setOpen] = useState(false);
	const openTimer = useRef<number | null>(null);
	const closeTimer = useRef<number | null>(null);
	const wrapRef = useRef<HTMLDivElement>(null);

	const hasItems = !!items && items.length > 0;

	function clearTimers() {
		if (openTimer.current) window.clearTimeout(openTimer.current);
		if (closeTimer.current) window.clearTimeout(closeTimer.current);
		openTimer.current = null;
		closeTimer.current = null;
	}

	function scheduleOpen() {
		if (!hasItems) return;
		clearTimers();
		openTimer.current = window.setTimeout(() => setOpen(true), OPEN_DELAY);
	}

	function scheduleClose() {
		clearTimers();
		closeTimer.current = window.setTimeout(() => setOpen(false), CLOSE_DELAY);
	}

	// Close panel on route change — derive-during-render pattern
	useCloseOnPathnameChange(setOpen, clearTimers);

	// Close on Escape
	useEffect(() => {
		if (!open) return;
		function onKey(e: KeyboardEvent) {
			if (e.key === "Escape") setOpen(false);
		}
		window.addEventListener("keydown", onKey);
		return () => window.removeEventListener("keydown", onKey);
	}, [open]);

	// Close on outside click
	useEffect(() => {
		if (!open) return;
		function onClick(e: MouseEvent) {
			if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
				setOpen(false);
			}
		}
		window.addEventListener("mousedown", onClick);
		return () => window.removeEventListener("mousedown", onClick);
	}, [open]);

	return (
		<div
			ref={wrapRef}
			className="relative"
			onMouseEnter={scheduleOpen}
			onMouseLeave={scheduleClose}
			onFocus={scheduleOpen}
			onBlur={(e) => {
				// Only schedule close if focus leaves the entire wrapper
				if (!e.currentTarget.contains(e.relatedTarget as Node)) scheduleClose();
			}}
		>
			<LinkWithChannel
				href={href}
				className={cn(
					"relative inline-flex items-center gap-1.5 px-2.5 py-2 font-mono text-[0.78rem] font-medium uppercase tracking-[0.16em] text-muted-foreground transition-colors duration-150 hover:text-primary focus:text-primary focus:outline-none lg:text-[0.82rem]",
					open && "text-primary",
				)}
				aria-haspopup={hasItems ? "menu" : undefined}
				aria-expanded={hasItems ? open : undefined}
			>
				{label}
				{hasItems && (
					<svg
						aria-hidden
						viewBox="0 0 12 12"
						className={cn(
							"h-2.5 w-2.5 shrink-0 text-current opacity-50 transition-transform duration-200",
							open && "rotate-180 opacity-100",
						)}
						fill="none"
						stroke="currentColor"
						strokeWidth="1.5"
					>
						<path d="M3 4.5 L6 7.5 L9 4.5" strokeLinecap="round" strokeLinejoin="round" />
					</svg>
				)}
			</LinkWithChannel>

			{/* Underline indicator when open */}
			<span
				aria-hidden
				className={cn(
					"pointer-events-none absolute inset-x-2.5 -bottom-px h-px origin-left bg-primary transition-transform duration-200",
					open ? "scale-x-100" : "scale-x-0",
				)}
			/>

			{hasItems && (
				<div
					role="menu"
					className={cn(
						"pointer-events-none absolute left-0 top-full z-50 mt-3 w-[min(360px,90vw)] origin-top-left",
						"transition-[opacity,transform] duration-150",
						open ? "pointer-events-auto translate-y-0 opacity-100" : "-translate-y-1 opacity-0",
					)}
				>
					{/* Invisible hover bridge so cursor can travel from trigger to panel */}
					<span aria-hidden className="absolute -top-3 left-0 right-0 h-3" />

					<div className="relative overflow-hidden rounded-xl border border-border bg-background shadow-[0_25px_60px_-20px_rgba(8,39,74,0.25)]">
						{/* Faint blueprint raster */}
						<div
							aria-hidden
							className="pointer-events-none absolute inset-0 opacity-[0.04]"
							style={{
								backgroundImage:
									"linear-gradient(to right, var(--primary) 1px, transparent 1px), linear-gradient(to bottom, var(--primary) 1px, transparent 1px)",
								backgroundSize: "24px 24px",
							}}
						/>
						{/* Corner crosshairs */}
						<svg
							aria-hidden
							viewBox="0 0 12 12"
							className="text-primary/30 pointer-events-none absolute left-1.5 top-1.5 h-2.5 w-2.5"
							fill="none"
							stroke="currentColor"
							strokeWidth="1"
						>
							<path d="M0 4 V0 H4" />
						</svg>
						<svg
							aria-hidden
							viewBox="0 0 12 12"
							className="text-primary/30 pointer-events-none absolute right-1.5 top-1.5 h-2.5 w-2.5 rotate-90"
							fill="none"
							stroke="currentColor"
							strokeWidth="1"
						>
							<path d="M0 4 V0 H4" />
						</svg>

						{/* Eyebrow header */}
						<div className="border-border/70 relative flex items-center justify-between border-b px-4 pb-2 pt-3">
							<span className="font-mono text-[0.55rem] uppercase tracking-[0.2em] text-primary">
								{label} · {sectionLabel}
							</span>
							<span className="font-mono text-[0.5rem] uppercase tabular-nums tracking-[0.18em] text-muted-foreground">
								{String(items.length).padStart(2, "0")}
							</span>
						</div>

						<ul className="relative py-1">
							{items.map((item, idx) => (
								<li key={item.href}>
									<LinkWithChannel
										href={item.href}
										role="menuitem"
										onClick={() => setOpen(false)}
										className="group/item hover:bg-primary/[0.045] relative flex items-start gap-3 px-4 py-2.5 transition-colors"
									>
										<span className="mt-0.5 font-mono text-[0.55rem] uppercase tabular-nums tracking-[0.18em] text-muted-foreground">
											{String(idx + 1).padStart(2, "0")}
										</span>
										<span className="min-w-0 flex-1">
											<span className="group-hover/item:text-primary block font-display text-[0.92rem] font-medium leading-tight tracking-[-0.01em] text-foreground transition-colors">
												{item.label}
											</span>
											{item.sub && (
												<span className="mt-0.5 block text-[0.78rem] leading-snug text-muted-foreground">
													{item.sub}
												</span>
											)}
										</span>
										<svg
											aria-hidden
											viewBox="0 0 16 16"
											fill="none"
											stroke="currentColor"
											strokeWidth="1.5"
											className="group-hover/item:translate-x-0.5 group-hover/item:opacity-100 group-hover/item:text-primary mt-1 h-3 w-3 text-muted-foreground opacity-0 transition-all"
										>
											<path d="M4 12 L12 4 M6 4 H12 V10" strokeLinecap="round" strokeLinejoin="round" />
										</svg>
									</LinkWithChannel>
								</li>
							))}
						</ul>

						{footer && (
							<div className="border-border/70 relative border-t">
								<LinkWithChannel
									href={footer.href}
									onClick={() => setOpen(false)}
									className="group/footer bg-primary/[0.02] hover:bg-primary/[0.06] flex items-center justify-between px-4 py-3 font-mono text-[0.6rem] uppercase tracking-[0.18em] text-primary transition-colors"
								>
									<span>{footer.label}</span>
									<svg
										aria-hidden
										viewBox="0 0 16 16"
										fill="none"
										stroke="currentColor"
										strokeWidth="1.5"
										className="group-hover/footer:translate-x-0.5 h-3.5 w-3.5 transition-transform"
									>
										<path d="M3 8 H13 M9 4 L13 8 L9 12" strokeLinecap="round" strokeLinejoin="round" />
									</svg>
								</LinkWithChannel>
							</div>
						)}
					</div>
				</div>
			)}
		</div>
	);
}
